import React, { createContext, useContext, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Cookies from "js-cookie";
import { useTranslation } from 'react-i18next';
import '../styles/Notification.css';
import { getApiUrl } from "../utils/config";

const AuthContext = createContext();
const API_URL = getApiUrl();

export const AuthProvider = ({ children }) => {
    const [isAuth, setIsAuth] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: "", type: "" });
    const { t } = useTranslation();

    useEffect(() => {
        checkAuth();
    }, []);

    useEffect(() => {
        if (isAuth) return; // Уже авторизован — ничего не делаем

        // Проверяем наличие Telegram WebApp
        if (window.Telegram && window.Telegram.WebApp) {
            const tg = window.Telegram.WebApp;
            const initData = tg.initData;
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // Логируем все доступные данные для отладки
            console.log('🔍 Telegram WebApp detected:', {
                initData: initData ? initData.substring(0, 50) + '...' : 'empty',
                initDataUnsafe: tg.initDataUnsafe,
                platform: tg.platform,
                version: tg.version,
                isExpanded: tg.isExpanded
            });

            if (typeof window !== 'undefined' && window.addLog) {
                window.addLog(`Telegram WebApp: initData=${initData ? 'есть' : 'нет'}, platform=${tg.platform}, version=${tg.version}`);
            }

            // Если есть initData, пытаемся авторизоваться
            if (initData && initData.trim() !== '') {
                if (typeof window !== 'undefined' && window.addLog) window.addLog('initData найден: ' + initData);
                fetch('/api/auth/telegram-webapp', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ initData, timezone }),
                    credentials: 'include'  // Обязательно для сохранения cookies
                })
                    .then(res => res.json())
                    .then(data => {
                        if (typeof window !== 'undefined' && window.addLog) window.addLog('Ответ от /api/auth/telegram-webapp: ' + JSON.stringify(data));
                        if (data.success) {
                            if (typeof window !== 'undefined' && window.addLog) window.addLog('Успешная авторизация через Telegram WebApp, вызываем checkAuth');
                            checkAuth();
                        } else {
                            if (typeof window !== 'undefined' && window.addLog) window.addLog('Ошибка авторизации через Telegram WebApp: ' + JSON.stringify(data));
                            console.log('❌ Telegram WebApp auth failed:', data);
                        }
                    })
                    .catch(err => {
                        if (typeof window !== 'undefined' && window.addLog) window.addLog('Ошибка запроса к /api/auth/telegram-webapp: ' + err);
                        console.error('❌ Telegram WebApp auth error:', err);
                    });
            } else {
                // initData пустой или отсутствует
                console.log('⚠️ Telegram WebApp initData пустой или отсутствует');
                if (typeof window !== 'undefined' && window.addLog) {
                    window.addLog('initData пустой, попытка использовать initDataUnsafe');
                }

                // initData недоступен - это может быть нормально в зависимости от платформы
                console.log('ℹ️ initData недоступен. Пользователь может войти обычным способом.');
                if (tg.initDataUnsafe && tg.initDataUnsafe.user) {
                    console.log('🔧 initDataUnsafe доступен (но не используется для безопасности):', {
                        user_id: tg.initDataUnsafe.user.id,
                        username: tg.initDataUnsafe.user.username
                    });
                }
            }
        } else {
            console.log('❌ Telegram WebApp не обнаружен');
        }
    }, [isAuth]);

    const checkAuth = async () => {
        if (typeof window !== 'undefined' && window.addLog) window.addLog('checkAuth: вызов');
        setIsLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/auth-check`, {
                method: "GET",
                credentials: "include",
            });
            if (typeof window !== 'undefined' && window.addLog) window.addLog('checkAuth: статус ответа ' + response.status);
            console.log('[checkAuth] Ответ сервера:', response);
            let data = null;
            let showSessionExpired = false;
            if (response.status === 401 || response.status === 403 || response.status === 429) {
                try {
                    data = await response.json();
                    if (data && data.logout) showSessionExpired = true;
                } catch { }
                if (typeof window !== 'undefined' && window.addLog) window.addLog('checkAuth: не авторизован (status ' + response.status + ')');
                if (showSessionExpired) {
                    console.log('[checkAuth] Не авторизован, вызываю logout(false,', showSessionExpired, ')');
                    await logout(false, showSessionExpired); // Без reload!
                } else {
                    setIsAuth(false);
                    setIsAdmin(false);
                }
                setIsLoading(false);
                return;
            }

            data = await response.json();
            if (typeof window !== 'undefined' && window.addLog) window.addLog('Ответ от /api/auth-check: ' + JSON.stringify(data));
            console.log('[checkAuth] Данные от сервера:', data);
            setIsAuth(data.success);
            setIsAdmin(data.isAdmin || false);
        } catch (error) {
            if (typeof window !== 'undefined' && window.addLog) window.addLog('checkAuth: ошибка ' + error);
            console.error("Ошибка проверки авторизации:", error);
            if (typeof window !== 'undefined' && window.addLog) window.addLog('checkAuth: вызываю logout(false, true) из catch');
            await logout(false, true); // Без reload!
        } finally {
            if (typeof window !== 'undefined' && window.addLog) window.addLog('checkAuth: завершено, isAuth=' + isAuth + ', isAdmin=' + isAdmin + ', isLoading=' + isLoading);
            setIsLoading(false);
        }
    };

    const logout = async (showReload = true, showSessionExpired = false) => {
        try {
            if (typeof window !== 'undefined' && window.addLog) window.addLog('logout: вызов');
            console.log('[logout] Запрос на /api/logout');
            await fetch(`${API_URL}/api/logout`, {
                method: "POST",
                credentials: "include"
            });
            Cookies.remove('token');
            setIsAuth(false);
            setIsAdmin(false);
            if (showReload) {
                if (typeof window !== 'undefined' && window.addLog) window.addLog('logout: window.location.reload()');
                console.log('[logout] window.location.reload()');
                window.location.reload();
            } else if (showSessionExpired) {
                showNotification('Ваша сессия истекла. Пожалуйста, войдите заново.', 'error');
            }
        } catch (error) {
            console.error("Ошибка при выходе:", error);
            if (typeof window !== 'undefined' && window.addLog) window.addLog('logout: ошибка ' + error);
        }
    };

    const showNotification = (message, type = "info") => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: "", type: "" });
        }, 3000);
    };

    return (
        <AuthContext.Provider value={{
            isAuth,
            isLoading,
            isAdmin,
            setIsAuth,
            logout,
            checkAuth,
            userRole,
            setUserRole,
            showNotification,
            notification
        }}>
            {children}
            {notification.show && ReactDOM.createPortal(
                <div className={`notification ${notification.type}`}>
                    {notification.message}
                </div>,
                document.body
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
